import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Checkbox, Icon, Modal, Notification, RadioCardGroup, Spinner } from "@telia/teddy";
import { useDispatch, useSelector } from "react-redux";
import PAGES from "../../../../../constants/pages";
import { SHSignStatus } from "../../../../../models/server/signatureHandler";
import { API_URL_PRINT_SIGN_DOCUMENTS } from "../../api";
import {
    confirmManualSignature,
    fetchSignatureMethods,
    getSignDocumentStatus,
    requestSignDocument,
} from "../../state/actions";
import { SignatureMethodEnum } from "../../types/enums";
import { getAgentChainName } from "../../../app/state/selectors";
import "./SignatureMethodsModal.less";
import _isEmpty from "lodash/isEmpty";
import {
    SIGNATURE_METHOD_LABELS,
    SIGNATURE_METHOD_LABELS_ELKJOP,
    DOCUMENT_TYPE_LABELS,
    getManualSignButtonText,
    getManualSignAriaLabel,
} from "../signatureLabels";

const STOP_POLLING_SIGN_STATUSES: SHSignStatus[] = [
    "signed",
    "queued_for_automatic_closure",
    "closed",
    "canceled",
    "rejected",
    "document_error",
    "ssn_validation_failed",
];

const isTerminalSignStatus = (status?: string | null): boolean =>
    !!status && STOP_POLLING_SIGN_STATUSES.includes(status.toLowerCase() as SHSignStatus);

const isCompletedSignStatus = (status?: string | null): boolean => {
    if (!status) {
        return false;
    }

    const normalizedStatus = status.toLowerCase();
    return (
        normalizedStatus === "signed" ||
        normalizedStatus === "queued_for_automatic_closure" ||
        normalizedStatus === "closed"
    );
};

interface Props {
    isModalOpen: boolean;
    setIsModalOpen: (isOpen: boolean) => void;
}

const SignatureMethodsModal: React.FC<Props> = ({ isModalOpen, setIsModalOpen }) => {
    const dispatch = useDispatch();
    const agentChainName = useSelector(getAgentChainName);
    const isElkjop = agentChainName?.toLowerCase().includes("elkj") ?? false;
    const signatureMethodLabels = isElkjop ? SIGNATURE_METHOD_LABELS_ELKJOP : SIGNATURE_METHOD_LABELS;
    const [isOwnerPresent, setIsOwnerPresent] = useState(true);
    const [methodsFetchInitiated, setMethodsFetchInitiated] = useState(false);
    const [signatureMethods, setSignatureMethods] = useState<Array<{ signature_method: SignatureMethodEnum }>>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isRequesting, setIsRequesting] = useState(false);
    const [activeRequestMethod, setActiveRequestMethod] = useState<SignatureMethodEnum | null>(null);
    const [signStatus, setSignStatus] = useState<string | null>(null);
    const [signCode, setSignCode] = useState<string | null>(null);
    const [manualSignClicked, setManualSignClicked] = useState(false);
    const [isManualSignatureConfirmed, setIsManualSignatureConfirmed] = useState(false);
    const [isAwaitingSigning, setIsAwaitingSigning] = useState(false);
    const [isRefreshingSignStatus, setIsRefreshingSignStatus] = useState(false);
    const [selectedSigningMethod, setSelectedSigningMethod] = useState<SignatureMethodEnum | null>(null);
    const [assentlyDocuments, setAssentlyDocuments] = useState<Array<{ id: string; type: string }> | null>(null);
    const [selectedDocIds, setSelectedDocIds] = useState(new Set<string>());
    const [hasOpenedDocuments, setHasOpenedDocuments] = useState(false);
    const [signOrderRef, setSignOrderRef] = useState<string | null>(null);
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const isManualSignSessionRef = useRef(false);

    const refreshSignStatus = useCallback(async () => {
        const statusResult = await dispatch(getSignDocumentStatus());
        setError(null);
        const newStatus = statusResult?.status;

        if (newStatus) {
            setSignStatus(newStatus);
            if (isTerminalSignStatus(newStatus)) {
                setIsAwaitingSigning(false);
            }
            if (STOP_POLLING_SIGN_STATUSES.includes(newStatus.toLowerCase() as SHSignStatus)) {
                if (
                    (newStatus.toLowerCase() === "signed" ||
                        newStatus.toLowerCase() === "queued_for_automatic_closure" ||
                        newStatus.toLowerCase() === "closed") &&
                    (statusResult?.orderRef ?? signOrderRef)
                ) {
                    if (isManualSignSessionRef.current) {
                        try {
                            await dispatch(confirmManualSignature());
                        } catch (e: any) {
                            setError(e?.displayable ? e.error : "Kunne ikke bekrefte signatur. Vennligst prøv igjen.");
                            throw e;
                        }
                    }
                    window.location.assign(
                        `${PAGES.RETAILER.ORDER.RECEIPT.URL}?orderRef=${statusResult?.orderRef ?? signOrderRef}`,
                    );
                }
            }
        }

        return statusResult;
    }, [dispatch, signOrderRef]);

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, []);

    const startPolling = useCallback(() => {
        stopPolling();
        pollingIntervalRef.current = setInterval(async () => {
            try {
                const statusResult = await refreshSignStatus();
                if (isTerminalSignStatus(statusResult?.status)) {
                    stopPolling();
                }
            } catch {
                // Avoid unhandled promise rejections from polling; user can manually refresh.
            }
        }, 30000);
    }, [refreshSignStatus, stopPolling]);

    const handleMethodSelect = useCallback(
        async (method: SignatureMethodEnum) => {
            if (
                method === SignatureMethodEnum.SIGN_ONLY ||
                method === SignatureMethodEnum.REMOTE_ONE_TIME_PIN_AND_BANK_ID ||
                method === SignatureMethodEnum.REMOTE_BANK_ID ||
                method === SignatureMethodEnum.BANK_ID ||
                method === SignatureMethodEnum.ID_PHOTO
            ) {
                setIsRequesting(true);
                setActiveRequestMethod(method);
                setSelectedSigningMethod(method);
                setError(null);
                setSignStatus(null);
                try {
                    const result = await dispatch(requestSignDocument(method));
                    setSignStatus(result?.status ?? null);

                    if (result?.documents && result.documents.length > 0) {
                        setAssentlyDocuments(result.documents);
                        setSelectedDocIds(new Set(result.documents.map((d) => d.id)));
                    }

                    if (
                        !isElkjop &&
                        (method === SignatureMethodEnum.SIGN_ONLY ||
                            method === SignatureMethodEnum.BANK_ID ||
                            method === SignatureMethodEnum.ID_PHOTO) &&
                        result?.session_id
                    ) {
                        const last6 = result.session_id.slice(-6);
                        setSignCode(`${last6.slice(0, 3)}-${last6.slice(3)}`);
                    }

                    if (
                        method === SignatureMethodEnum.REMOTE_BANK_ID ||
                        method === SignatureMethodEnum.BANK_ID ||
                        method === SignatureMethodEnum.ID_PHOTO ||
                        method === SignatureMethodEnum.REMOTE_ONE_TIME_PIN_AND_BANK_ID ||
                        method === SignatureMethodEnum.SIGN_ONLY
                    ) {
                        const shouldAwait = !isTerminalSignStatus(result?.status ?? null);
                        setIsAwaitingSigning(shouldAwait);
                        if (shouldAwait) {
                            startPolling();
                        }
                    }
                } catch (e: any) {
                    setError(
                        e?.displayable ? e.error : "Kunne ikke sende signeringsforespørsel. Vennligst prøv igjen.",
                    );
                } finally {
                    setIsRequesting(false);
                    setActiveRequestMethod(null);
                }
            }
        },
        [dispatch, isElkjop, startPolling],
    );

    const fetchMethods = useCallback(async () => {
        setError(null);
        setSignatureMethods([]);
        setIsLoading(true);
        try {
            const ownerPresent = isElkjop ? undefined : isOwnerPresent;
            const result = await dispatch(fetchSignatureMethods(ownerPresent));
            const methods = result?.signatureMethods || [];
            setSignatureMethods(methods);

            if (!isElkjop && !isOwnerPresent && methods.length === 1) {
                await handleMethodSelect(methods[0].signature_method);
            } else {
                setMethodsFetchInitiated(true);
            }
        } catch (e: any) {
            setError(e?.displayable ? e.error : "Kunne ikke hente signeringsmetoder. Vennligst prøv igjen.");
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, handleMethodSelect, isElkjop, isOwnerPresent]);

    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    useEffect(() => {
        if (isModalOpen) {
            setError(null);
            setSignStatus(null);
            setSignCode(null);
            setIsRequesting(false);
            setActiveRequestMethod(null);
            setManualSignClicked(false);
            setIsManualSignatureConfirmed(false);
            setIsAwaitingSigning(false);
            setIsRefreshingSignStatus(false);
            setSelectedSigningMethod(null);
            isManualSignSessionRef.current = false;
            setAssentlyDocuments(null);
            setSelectedDocIds(new Set());
            setIsOwnerPresent(true);
            setSignOrderRef(null);
            if (isElkjop) {
                setMethodsFetchInitiated(true);
                fetchMethods();
            } else {
                setMethodsFetchInitiated(false);
                setSignatureMethods([]);
            }
            // On modal open, check if there is already an active signing session.
            // If awaiting_approval, skip showing methods and go directly to the accept state.
            // Applies to both Assently and Scrive — mirrors Salsa's SummaryPageFlow dialog-open check
            // (signatureMethods.size() > 1 branch covers both when multiple methods are present).
            dispatch(getSignDocumentStatus())
                .then((statusResult) => {
                    if (statusResult?.status?.toLowerCase() === "awaiting_approval") {
                        setSignStatus(statusResult.status);
                        if (statusResult?.orderRef) {
                            setSignOrderRef(statusResult.orderRef);
                        }
                        setIsAwaitingSigning(true);
                        startPolling();
                    }
                })
                .catch(() => {
                    // No active session — expected on first open
                });
        } else {
            stopPolling();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModalOpen]);

    const handleBack = () => {
        stopPolling();
        setError(null);
        setSignStatus(null);
        setSignCode(null);
        setSignOrderRef(null);
        setIsRequesting(false);
        setActiveRequestMethod(null);
        setIsAwaitingSigning(false);
        setIsRefreshingSignStatus(false);
        setSelectedSigningMethod(null);
        isManualSignSessionRef.current = false;
        setAssentlyDocuments(null);
        setSelectedDocIds(new Set());
        setHasOpenedDocuments(false);

        if (!isOwnerPresent) {
            setMethodsFetchInitiated(false);
            setSignatureMethods([]);
        }
    };

    const handleClose = () => {
        handleBack();
        setManualSignClicked(false);
        setIsManualSignatureConfirmed(false);
        setIsModalOpen(false);
    };

    const isSigningInProgress = !!signStatus || !!signCode || isAwaitingSigning;

    return (
        <Modal defaultOpen open={isModalOpen}>
            <Modal.Overlay>
                <Modal.Content>
                    <Modal.Title as="h3">{manualSignClicked ? "Papirsignering" : "Signering"}</Modal.Title>
                    <Modal.Close aria-label="lukk" slot="floating" onClick={handleClose} />
                    <Modal.Group direction="column">
                        {!!error && (
                            <Notification type="tile" variant="error" mt="100">
                                <Notification.Icon />
                                <Notification.Text>{error}</Notification.Text>
                            </Notification>
                        )}
                        {!isElkjop && !isSigningInProgress && !manualSignClicked && !methodsFetchInitiated && (
                            <>
                                <RadioCardGroup
                                    value={isOwnerPresent ? "owner-present" : "remote-signing"}
                                    onValueChange={(value) => {
                                        const ownerPresent = value === "owner-present";
                                        setIsOwnerPresent(ownerPresent);
                                        setMethodsFetchInitiated(false);
                                        setSignatureMethods([]);
                                        setError(null);
                                    }}
                                    orientation="vertical"
                                    name="signature-owner-presence"
                                    className="signature-methods-modal__radio-card-group"
                                >
                                    <RadioCardGroup.Item value="owner-present">
                                        <div className="signature-methods-modal__radio-card-header">
                                            <Icon name="end-user" size="sm" aria-hidden="true" focusable="false" />
                                            <RadioCardGroup.Title className="signature-methods-modal__radio-title">
                                                Eier er tilstede
                                            </RadioCardGroup.Title>
                                        </div>
                                        <RadioCardGroup.Body>
                                            Signering med nettbrett eller på papir
                                        </RadioCardGroup.Body>
                                    </RadioCardGroup.Item>
                                    <RadioCardGroup.Item value="remote-signing">
                                        <div className="signature-methods-modal__radio-card-header">
                                            <Icon name="wireless" size="sm" aria-hidden="true" focusable="false" />
                                            <RadioCardGroup.Title className="signature-methods-modal__radio-title">
                                                Fjernsignering
                                            </RadioCardGroup.Title>
                                        </div>
                                        <RadioCardGroup.Body>
                                            Eier vil motta en SMS med en lenke for signering
                                        </RadioCardGroup.Body>
                                    </RadioCardGroup.Item>
                                </RadioCardGroup>
                                <Button
                                    variant="primary"
                                    mt="200"
                                    loading={isLoading || isRequesting}
                                    onClick={async () => {
                                        await fetchMethods();
                                    }}
                                >
                                    Neste
                                </Button>
                            </>
                        )}
                        {isElkjop && isLoading && (
                            <div className="signature-methods-modal__loading-container">
                                <Spinner size="lg" />
                                <p>Henter signeringsmetoder</p>
                            </div>
                        )}
                        {!isLoading && !error && signatureMethods.length === 0 && methodsFetchInitiated && (
                            <p>Ingen signeringsmetoder tilgjengelig.</p>
                        )}
                        {!isLoading &&
                            !error &&
                            signatureMethods.length > 0 &&
                            !manualSignClicked &&
                            !isSigningInProgress && (
                                <>
                                    {signatureMethods
                                        .filter((method) => method.signature_method !== SignatureMethodEnum.MANUAL_SIGN)
                                        .map((method) => (
                                            <Button
                                                key={method.signature_method}
                                                variant="secondary"
                                                size="sm"
                                                style={{ alignSelf: "flex-start" }}
                                                onClick={() => handleMethodSelect(method.signature_method)}
                                                loading={activeRequestMethod === method.signature_method}
                                                disabled={isRequesting || !!signStatus}
                                                mt="100"
                                                width="100%"
                                            >
                                                {signatureMethodLabels[method.signature_method] ??
                                                    method.signature_method}
                                            </Button>
                                        ))}
                                    {signatureMethods.some(
                                        (method) => method.signature_method === SignatureMethodEnum.MANUAL_SIGN,
                                    ) && (
                                        <Button
                                            key={SignatureMethodEnum.MANUAL_SIGN}
                                            variant="secondary"
                                            size="sm"
                                            style={{ alignSelf: "flex-start" }}
                                            aria-label={getManualSignAriaLabel(isElkjop)}
                                            onClick={async () => {
                                                setIsRequesting(true);
                                                setActiveRequestMethod(SignatureMethodEnum.MANUAL_SIGN);
                                                setSelectedSigningMethod(SignatureMethodEnum.MANUAL_SIGN);
                                                isManualSignSessionRef.current = true;
                                                setError(null);
                                                try {
                                                    const result = await dispatch(requestSignDocument("MANUAL_SIGN"));
                                                    if (result?.documents && result.documents.length > 0) {
                                                        setAssentlyDocuments(result.documents);
                                                        setSelectedDocIds(new Set(result.documents.map((d) => d.id)));
                                                    }
                                                    setManualSignClicked(true);
                                                    setIsManualSignatureConfirmed(false);
                                                } catch (e: any) {
                                                    setError(
                                                        e?.displayable
                                                            ? e.error
                                                            : "Kunne ikke starte signeringsprosess. Vennligst prøv igjen.",
                                                    );
                                                } finally {
                                                    setIsRequesting(false);
                                                    setActiveRequestMethod(null);
                                                }
                                            }}
                                            loading={activeRequestMethod === SignatureMethodEnum.MANUAL_SIGN}
                                            disabled={isRequesting || !!signStatus}
                                            mt="100"
                                            width="100%"
                                        >
                                            <Icon name="document-pdf" size="sm" aria-hidden="true" focusable="false" />
                                            {getManualSignButtonText(isElkjop)}
                                        </Button>
                                    )}
                                </>
                            )}
                        {!isElkjop && methodsFetchInitiated && !manualSignClicked && !isSigningInProgress && (
                            <Button
                                className="signature-methods-modal__back-button"
                                variant="tertiary-purple"
                                onClick={() => {
                                    setMethodsFetchInitiated(false);
                                    setSignatureMethods([]);
                                    setError(null);
                                }}
                                mt="200"
                            >
                                <Icon name="arrow-left" size="sm" aria-hidden="true" focusable="false" />
                                Tilbake
                            </Button>
                        )}
                        {selectedSigningMethod &&
                            !activeRequestMethod &&
                            assentlyDocuments &&
                            assentlyDocuments.length > 0 && (
                                <>
                                    <p className="signature-methods-modal__documents-title">
                                        Dokumenter tilgjengelig for signering:
                                    </p>
                                    <ul className="signature-methods-modal__documents-list">
                                        {assentlyDocuments.map((doc) => (
                                            <li key={doc.id} className="signature-methods-modal__documents-list-item">
                                                <div className="signature-methods-modal__document-selection">
                                                    <Checkbox className="signature-methods-modal__document-checkbox">
                                                        <Checkbox.Trigger
                                                            checked={selectedDocIds.has(doc.id)}
                                                            onCheckedChange={() => {
                                                                setSelectedDocIds((prev) => {
                                                                    const next = new Set(prev);
                                                                    if (next.has(doc.id)) {
                                                                        next.delete(doc.id);
                                                                    } else {
                                                                        next.add(doc.id);
                                                                    }
                                                                    return next;
                                                                });
                                                            }}
                                                        />
                                                        <span className="signature-methods-modal__document-label">
                                                            <Icon
                                                                name="document-pdf"
                                                                size="sm"
                                                                aria-hidden="true"
                                                                focusable="false"
                                                            />
                                                            {DOCUMENT_TYPE_LABELS[doc.type] ?? doc.type}
                                                        </span>
                                                    </Checkbox>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                    <Button
                                        variant="tertiary-purple"
                                        mt="100"
                                        mb="300"
                                        disabled={selectedDocIds.size === 0}
                                        onClick={() => {
                                            const ids = Array.from(selectedDocIds).join(",");
                                            const newWindow = window.open(
                                                `${API_URL_PRINT_SIGN_DOCUMENTS}?ids=${ids}`,
                                                "_blank",
                                                "noopener,noreferrer",
                                            );
                                            if (newWindow) {
                                                newWindow.opener = null;
                                            }
                                            setHasOpenedDocuments(true);
                                        }}
                                    >
                                        <Icon name="document-pdf" size="sm" aria-hidden="true" focusable="false" />
                                        Åpne og skriv ut valgte dokumenter ({selectedDocIds.size}/
                                        {assentlyDocuments.length})
                                    </Button>
                                </>
                            )}

                        {isAwaitingSigning &&
                            isOwnerPresent &&
                            !isRefreshingSignStatus &&
                            !isCompletedSignStatus(signStatus) &&
                            (hasOpenedDocuments || !assentlyDocuments?.length) && (
                                <Notification type="card" variant="information" mt="100">
                                    <Notification.Heading as="h3">
                                        <Notification.Icon name="info">
                                            <strong>Status:</strong> Venter på signering
                                        </Notification.Icon>
                                    </Notification.Heading>

                                    {signCode && <Notification.Text>Signeringskode: {signCode}</Notification.Text>}
                                </Notification>
                            )}
                        {isAwaitingSigning &&
                            isOwnerPresent &&
                            _isEmpty(assentlyDocuments) &&
                            !isTerminalSignStatus(signStatus) && (
                                <div className="signature-methods-modal__status-spinner">
                                    <Spinner size="lg" />
                                </div>
                            )}
                        {isAwaitingSigning &&
                            !isOwnerPresent &&
                            !isTerminalSignStatus(signStatus) &&
                            !isRefreshingSignStatus && (
                                <>
                                    <Notification type="tile" variant="information" mt="100" mb="200">
                                        <Notification.Icon />
                                        <Notification.Text>
                                            {!isOwnerPresent
                                                ? "SMS er sendt til eier. Venter på at eier skal signere."
                                                : "Status: Venter på signering"}
                                        </Notification.Text>
                                    </Notification>
                                    <div className="signature-methods-modal__status-spinner">
                                        <Spinner size="lg" />
                                    </div>
                                </>
                            )}
                        {isCompletedSignStatus(signStatus) && (
                            <Notification type="tile" variant="success" mt="100">
                                <Notification.Icon />
                                <Notification.Text>Signering fullført</Notification.Text>
                            </Notification>
                        )}
                        {manualSignClicked && (
                            <>
                                {assentlyDocuments && assentlyDocuments.length > 0 && <hr />}
                                <div className="signature-methods-modal__manual-sign-confirmation">
                                    <Checkbox>
                                        <Checkbox.Trigger
                                            checked={isManualSignatureConfirmed}
                                            onCheckedChange={(checked) => {
                                                setIsManualSignatureConfirmed(checked === true);
                                            }}
                                        />
                                        <span>Jeg bekrefter at kontrakten er signert</span>
                                    </Checkbox>
                                </div>
                                <div className="signature-methods-modal__manual-sign-actions">
                                    <Button
                                        variant="tertiary-purple"
                                        onClick={() => {
                                            setManualSignClicked(false);
                                            setIsManualSignatureConfirmed(false);
                                            setSelectedSigningMethod(null);
                                            setError(null);
                                        }}
                                        mt="100"
                                    >
                                        <Icon name="arrow-left" size="sm" aria-hidden="true" focusable="false" />
                                        Tilbake
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={async () => {
                                            setIsRequesting(true);
                                            setError(null);
                                            try {
                                                const result = await dispatch(confirmManualSignature());
                                                handleClose();
                                                if (result?.status === "confirmed" && result?.orderRef) {
                                                    window.location.assign(
                                                        `${PAGES.RETAILER.ORDER.RECEIPT.URL}?orderRef=${result.orderRef}`,
                                                    );
                                                }
                                            } catch (e: any) {
                                                setError(
                                                    e?.displayable
                                                        ? e.error
                                                        : "Kunne ikke bekrefte signatur. Vennligst prøv igjen.",
                                                );
                                            } finally {
                                                setIsRequesting(false);
                                            }
                                        }}
                                        loading={isRequesting}
                                        disabled={!isManualSignatureConfirmed || isRequesting}
                                    >
                                        Neste
                                    </Button>
                                </div>
                            </>
                        )}
                        {(!!signCode || isAwaitingSigning) &&
                            !isTerminalSignStatus(signStatus) &&
                            (hasOpenedDocuments || !assentlyDocuments?.length) && (
                                <Button
                                    variant="expressive"
                                    loading={isRefreshingSignStatus}
                                    disabled={isRefreshingSignStatus}
                                    onClick={async () => {
                                        setIsRefreshingSignStatus(true);
                                        try {
                                            await refreshSignStatus();
                                        } catch {
                                            setError("Kunne ikke hente signeringsstatus. Vennligst prøv igjen.");
                                        } finally {
                                            setIsRefreshingSignStatus(false);
                                        }
                                    }}
                                    mt="100"
                                >
                                    Oppdater status
                                </Button>
                            )}
                        {(isSigningInProgress || (!!error && !methodsFetchInitiated)) &&
                            !STOP_POLLING_SIGN_STATUSES.includes((signStatus ?? "").toLowerCase() as SHSignStatus) && (
                                <Button
                                    variant="tertiary-purple"
                                    onClick={handleBack}
                                    disabled={isRefreshingSignStatus}
                                    mt="200"
                                >
                                    Avbryt
                                </Button>
                            )}
                    </Modal.Group>
                </Modal.Content>
            </Modal.Overlay>
        </Modal>
    );
};

export default SignatureMethodsModal;
